/**
 * One site's monthly reports, recipients and mailbox state.
 *
 * Read once, and again once a minute while the tab is visible and a report is
 * being built (the automatic build on the first of the month), so a row turns
 * ready on its own. A late answer for an earlier read never overwrites a
 * newer one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { anyRunning, getMonthly, type MonthlyCardResponse } from "../../../../lib/monitoringMonthly";
import { MONITOR_POLL_MS, useVisiblePoll } from "../useMonitors";

export function useMonthly(monitorId: string) {
  const [data, setData] = useState<MonthlyCardResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    try {
      const res = await getMonthly(monitorId);
      if (!alive.current || rid !== requestId.current) return;
      setData(res);
      setFailed(false);
    } catch {
      if (!alive.current || rid !== requestId.current) return;
      setFailed(true);
    }
  }, [monitorId]);

  useEffect(() => {
    setData(null);
    setFailed(false);
    void load();
  }, [load]);

  useVisiblePoll(anyRunning(data), MONITOR_POLL_MS, () => void load());

  return { data, failed, reload: load };
}
